/**
 * Navigation Configuration — Role-aware sidebar items (issue #132)
 *
 * Single source of truth for which sidebar items each role can see in the
 * PrivateLayout. PrivateLayout consumes this and renders only items whose
 * `roles` list contains the active profile.role.
 *
 * Role values match the literals stored in `profiles.role`:
 *   - 'admin'
 *   - 'supervisor'
 *   - 'technician'   (a.k.a. "Líder" when they own quotations / projects)
 *   - 'client'
 *
 * Items that don't apply to a role are simply omitted from this list.
 * If a route doesn't exist yet (e.g. `/supervisor`, `/mis-tareas`, `/portal-cliente`),
 * we do NOT add a nav item for it — Login.js handles the fallback redirect with
 * a TODO. Adding a link to a non-existent route would crash the user.
 *
 * Sections group items visually in the sidebar (only rendered when the
 * sidebar is expanded). Items inside a section appear in order.
 *
 * @module config/navConfig
 */

export const ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  TECHNICIAN: 'technician',
  CLIENT: 'client',
};

/**
 * Sidebar items. The order in this array is the rendering order.
 * Use `section` to insert a visual section divider before this item.
 *
 * @typedef {Object} NavItem
 * @property {string} path           - Route path (NavLink `to`)
 * @property {string} label          - Visible text when expanded
 * @property {string} icon           - Bootstrap icon class (e.g. 'bi-folder')
 * @property {string[]} roles        - Roles allowed to see this item
 * @property {string} [section]      - Optional section header to render BEFORE this item
 * @property {string} [title]        - Optional tooltip; defaults to label
 */

/** @type {NavItem[]} */
export const NAV_ITEMS = [
  // Top-level — always first, no section
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'bi-speedometer2',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR],
  },
  {
    path: '/mensajes',
    label: 'Mensajes',
    icon: 'bi-chat-dots',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.CLIENT],
  },

  // --- Operaciones ---
  {
    section: 'Operaciones',
    path: '/proyectos',
    label: 'Proyectos',
    icon: 'bi-folder',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.CLIENT],
  },
  {
    path: '/cotizaciones',
    label: 'Cotizaciones',
    icon: 'bi-file-earmark-text',
    // Admin + supervisor see the global cotizaciones list.
    // Clients see their own quotations under the same route — the list/page
    // is RLS-filtered server-side.
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.CLIENT],
  },
  {
    // Líder inbox (issue #131) — visible to technicians (líderes) AND admins
    // so admins can preview / debug what a líder sees.
    path: '/mis-cotizaciones',
    label: 'Mis Cotizaciones',
    icon: 'bi-inboxes',
    roles: [ROLES.ADMIN, ROLES.TECHNICIAN],
  },
  {
    path: '/tareas',
    label: 'Tareas',
    icon: 'bi-check2-square',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR],
  },
  {
    // Unified calendar (#465): tasks + milestones + payments + project bands
    // in one view. Same audience as Tareas (this data is internal, not
    // client-facing) — a client's own project dates/payments already
    // surface on their project detail page.
    //
    // TECHNICIAN added in #494: the epic (#490) decision table is explicit
    // that a technician's default view is "lo suyo" with an opt-in switch to
    // the team view — that's meaningless if a technician can never reach the
    // page. Also required unblocking the PrivateLayout technician redirect
    // (see technicianAllowedPaths in components/layout/PrivateLayout.js).
    path: '/calendario',
    label: 'Calendario',
    icon: 'bi-calendar3',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.TECHNICIAN],
  },
  {
    path: '/gastos',
    label: 'Gastos',
    icon: 'bi-cash',
    // El supervisor ve y registra gastos, no los edita ni los borra — así lo
    // dice la matriz y así lo permite la RLS (`supervisor_view_expenses`,
    // `supervisor_create_expenses`). Es el dato con el que decide compras.
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR],
  },
  {
    path: '/progreso',
    label: 'Progreso',
    icon: 'bi-camera',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.CLIENT],
  },

  // --- Comercial ---
  {
    section: 'Comercial',
    path: '/clientes',
    label: 'Clientes',
    icon: 'bi-building',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/oportunidades',
    label: 'Oportunidades',
    icon: 'bi-briefcase',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/facturas',
    label: 'Facturas',
    icon: 'bi-receipt-cutoff',
    // Supervisor incluido en #524. La matriz dice "Facturas / Ver todas:
    // Supervisor 👁" desde v0.22, pero el link nunca estuvo en su sidebar: la
    // ruta `/facturas` no tiene RoleGuard, asi que el supervisor SI podia
    // entrar tecleando la URL — solo que no habia forma de descubrirla. Sin
    // esta linea, la RLS que #524 le concede sobre `invoice_items` /
    // `invoice_payments` no arregla nada visible.
    // Solo lectura: `canEdit = isAdmin` en InvoiceListPage ya esconde "Nueva
    // Factura" y el borrado, que la matriz le niega ("Generar factura ❌",
    // "Registrar pago ❌").
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.CLIENT],
  },
  {
    path: '/solicitudes-cotizacion',
    label: 'Solicitudes de Cotización',
    icon: 'bi-file-earmark-ruled',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR],
  },

  // --- Almacén ---
  {
    section: 'Almacén',
    path: '/inventario',
    label: 'Inventario',
    icon: 'bi-boxes',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/compras',
    label: 'Compras',
    icon: 'bi-cart-check',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/proveedores',
    label: 'Proveedores',
    icon: 'bi-truck',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/herramientas',
    label: 'Herramientas',
    icon: 'bi-tools',
    // Admin manages. Supervisor: ve el catalogo y REGISTRA herramientas nuevas
    // (#798), sin editar ni asignar — el gate vive en la pagina (`ToolList`).
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR],
  },
  {
    // #798 — el supervisor da de alta materiales pero NO tiene la pantalla de
    // Inventario (`/inventario` sigue admin-only: listar, editar, lotes y
    // valuacion estan pendientes de decision). Sin esta entrada el alta que
    // la base y la ruta le conceden no tendria puerta. El admin no la ve: ya
    // llega desde Inventario.
    path: '/inventario/nuevo',
    label: 'Agregar material',
    icon: 'bi-box-seam',
    roles: [ROLES.SUPERVISOR],
  },
  {
    // Bandeja de aprobacion de solicitudes de herramienta (L13). Sin esta
    // entrada, la unica puerta a la pantalla es la notificacion: quien ya la
    // marco como leida no tiene forma de volver a la cola.
    //
    // #550: era ADMIN+SUPERVISOR. Aprobar es solo del admin, y esta bandeja no
    // hace otra cosa que aprobar y rechazar — dejarle el link al supervisor es
    // ofrecerle una puerta que rebota. Mismos roles que el RoleGuard de la ruta.
    path: '/herramientas/solicitudes',
    label: 'Solicitudes de Herramientas',
    icon: 'bi-clipboard-check',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/mis-herramientas',
    label: 'Mis Herramientas',
    icon: 'bi-tools',
    roles: [ROLES.TECHNICIAN],
  },
  {
    path: '/inventario/asignaciones',
    label: 'Asignaciones',
    icon: 'bi-person-check',
    roles: [ROLES.ADMIN],
  },
  {
    // #550: era ADMIN+SUPERVISOR (#132). Aprobar/rechazar una solicitud de
    // adicionales pasa a ser del admin, y la bandeja no tiene otras acciones.
    path: '/admin/solicitudes-materiales',
    label: 'Solicitudes',
    icon: 'bi-inbox',
    roles: [ROLES.ADMIN],
  },

  // --- Administración (admin-only block, formerly behind a divider) ---
  {
    section: 'Administración',
    path: '/usuarios',
    label: 'Usuarios',
    icon: 'bi-people',
    roles: [ROLES.ADMIN],
  },
  {
    path: '/admin/locations',
    label: 'Ubicaciones',
    icon: 'bi-geo-alt',
    roles: [ROLES.ADMIN],
  },
  {
    // Kane Atlas: enciclopedia 3D del conocimiento. Admin-only, igual que la ruta
    // (RoleGuard) y que el endpoint del servidor que sirve el grafo.
    path: '/admin/atlas',
    label: 'Kane Atlas',
    icon: 'bi-diagram-3',
    roles: [ROLES.ADMIN],
  },

  // --- Ayuda (visible to everyone authenticated) ---
  {
    section: 'Ayuda',
    path: '/ayuda',
    label: 'Ayuda',
    icon: 'bi-question-circle',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.TECHNICIAN, ROLES.CLIENT],
  },
  {
    // Tu agente: como conectarse (Kane OneDeck / ChatGPT) y el handbook de
    // habilidades por rol. Todos los roles; el contenido filtra por rol.
    path: '/agente',
    label: 'Tu agente',
    icon: 'bi-robot',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.TECHNICIAN, ROLES.CLIENT],
  },
  {
    path: '/novedades',
    label: 'Novedades',
    icon: 'bi-stars',
    roles: [ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.TECHNICIAN, ROLES.CLIENT],
  },
];

/**
 * Filter nav items for a given role.
 * @param {string} role - User role from profile.role
 * @returns {NavItem[]} Items visible to this role, in original order
 */
export const getNavItemsForRole = (role) => {
  if (!role) return [];
  return NAV_ITEMS.filter((item) => item.roles.includes(role));
};

/**
 * Landing-page redirect targets per role.
 *
 * If the canonical landing page doesn't exist yet, fall back to `/dashboard`
 * with a TODO breadcrumb so the redirect still lands the user somewhere safe.
 *
 * @param {string} role - User role
 * @returns {string} Path to redirect to after login
 */
export const getLandingPathForRole = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return '/dashboard';
    case ROLES.SUPERVISOR:
      // TODO(#132): replace with '/supervisor' once the supervisor dashboard
      // page exists (currently returns "Acceso No Autorizado").
      return '/dashboard';
    case ROLES.TECHNICIAN:
      // TODO(#132): replace with '/mis-tareas' once the líder landing exists.
      // For now, the technician portal dashboard is the canonical entry point.
      return '/tecnico/dashboard';
    case ROLES.CLIENT:
      // TODO(#132): replace with '/portal-cliente' once the client portal
      // landing exists. For now, the photo gallery is the closest existing
      // client-facing surface.
      return '/progreso/galeria';
    default:
      return '/dashboard';
  }
};

/**
 * Route-to-roles map for the RoleGuard component. Only routes that need
 * server-style hard gating are listed here. Other routes are filtered out
 * of the sidebar by `getNavItemsForRole` but remain reachable by URL —
 * RLS at the DB layer is the final defense.
 *
 * TODO(#132): expand coverage as we lock down more admin-only routes.
 *
 * @type {Record<string, string[]>} pathPrefix -> allowed roles
 */
export const ROUTE_ROLE_REQUIREMENTS = {
  '/usuarios': [ROLES.ADMIN],
  '/admin/locations': [ROLES.ADMIN],
  '/clientes': [ROLES.ADMIN],
  '/oportunidades': [ROLES.ADMIN],
  '/solicitudes-cotizacion': [ROLES.ADMIN, ROLES.SUPERVISOR],
  '/inventario': [ROLES.ADMIN],
  '/compras': [ROLES.ADMIN],
  '/proveedores': [ROLES.ADMIN],
  '/gastos': [ROLES.ADMIN, ROLES.SUPERVISOR],
};

const navConfig = {
  ROLES,
  NAV_ITEMS,
  getNavItemsForRole,
  getLandingPathForRole,
  ROUTE_ROLE_REQUIREMENTS,
};

export default navConfig;
